import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Button, Input, FormField, Alert, Spinner, Heading } from "@/components/ds";
import styles from "./ForgotPasswordForm.module.css";

export function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className={styles.form}>
        <Heading level={1} size="h3">Check your email</Heading>
        <Alert variant="success">
          If that email is registered, we've sent a reset link. It'll expire in 1 hour.
        </Alert>
        <p className={styles.footer}>
          <Link to="/login" className={styles.link}>Back to log in</Link>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <Heading level={1} size="h3">Forgot your password?</Heading>
      <p className={styles.description}>
        Enter your email and we'll send you a link to reset your password.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}

      <FormField label="Email" htmlFor="email">
        <Input
          type="email"
          name="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
        />
      </FormField>

      <Button type="submit" disabled={loading} fullWidth>
        {loading ? <Spinner size="sm" /> : "Send reset link"}
      </Button>

      <p className={styles.footer}>
        <Link to="/login" className={styles.link}>Back to log in</Link>
      </p>
    </form>
  );
}
